// Client-side error reporting via the Sentry browser SDK.
//
// Replaces the homemade /client_errors beacon. The SDK captures window errors
// and unhandled rejections natively — with real stack frames, breadcrumbs, and
// browser/OS context — and files them as platform:javascript, so they no
// longer masquerade as Ruby controller errors the way the relayed beacon did.
//
// Only first-party, actionable errors are forwarded. Third-party ad/analytics
// scripts (denyUrls), the opaque cross-origin "Script error." and benign
// browser notices (ignoreErrors), and content-free promise rejections
// ({} / null — common from Safari DOMExceptions) are dropped in the browser,
// before they leave, so they never cost quota.

import * as Sentry from "@sentry/browser";

const DSN =
  "https://71e8eb88c874286c3b06f65de47e9658@o4510965683322880.ingest.us.sentry.io/4510965687058432";

// Fraction of error events sent. 1.0 = all — safe here because the noise is
// removed by the filters below (not by sampling), and real first-party errors
// run at only tens per month against a much larger quota. Lower this one line
// if volume ever spikes.
const ERROR_SAMPLE_RATE = 1.0;

// Scripts we don't ship. A throw whose blame frame lives here is their bug, not
// ours. Sentry blames the top in-app frame, so a first-party error with a
// third-party frame lower in the stack is still kept.
export const THIRD_PARTY_DENY_URLS = [
  /googlesyndication\.com/,
  /googletagmanager\.com/,
  /google-analytics\.com/,
  /doubleclick\.net/,
  /adservice\.google/,
  /pagead2\./,
  /connect\.facebook\.net/,
  /static\.hotjar\.com/,
  /clarity\.ms/,
  /cloudflareinsights\.com/
];

export const IGNORE_ERRORS = [
  "Script error.", // opaque cross-origin — no message, file or stack to act on
  /ResizeObserver loop/, // benign browser layout notice
  // Browser-extension content scripts throw in-page, often with no stack for
  // denyUrls to blame. These WebExtensions APIs don't exist in our bundle.
  /runtime\.sendMessage/,
  /Extension context invalidated/,
  // In-app browsers (Snapchat, and others) inject a native bridge script into
  // the page before our bundle; when their bridge object is missing it throws
  // in global code of the document itself, so there is no URL to deny.
  /Can't find variable: SC\w*Bridge/,
  // Microsoft's email link scanner / Edge reading surfaces run the page with an
  // injected "update" RPC; its rejection names an object id we never created.
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/
];

function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

// "Error-like" values — DOMException (AbortError…), custom error objects — are
// not `instanceof Error` but carry a name/message. Safari serialises them to
// "{}", so we read the name/message directly to decide they're actionable.
function errorLikeLabel(value) {
  if (value == null || typeof value !== "object") return "";
  const name = typeof value.name === "string" ? value.name.trim() : "";
  const message = typeof value.message === "string" ? value.message.trim() : "";
  return name || message;
}

// A rejection is worth keeping only if the reason carries something to act on:
// a stack, a name, a message, or a non-empty serialised value. Bare {} / null /
// undefined / "" reasons name nothing and never will.
export function hasActionableReason(reason) {
  if (reason == null) return false;
  if (typeof reason === "string") return reason.trim().length > 0;
  if (typeof reason !== "object") return true; // numbers, booleans carry a value
  if (reason.stack || errorLikeLabel(reason)) return true;

  const serialised = safeStringify(reason);
  return Boolean(serialised) && serialised !== "{}" && serialised !== "[]" && serialised !== "null";
}

// Dev servers never send: localhost, *.test / *.local, and the 4731 dev port.
export function isDevHost(location = window.location) {
  const host = location.hostname;
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    /\.(test|local)$/.test(host) ||
    location.port === "4731"
  );
}

// Drop content-free unhandled rejections before they leave the browser. Only
// rejections are second-guessed here — other noise is handled by ignoreErrors /
// denyUrls, and a real thrown Error always has an actionable value.
export function beforeSend(event, hint) {
  const mechanism = event?.exception?.values?.[0]?.mechanism?.type;
  if (mechanism === "onunhandledrejection" && !hasActionableReason(hint?.originalException)) {
    return null;
  }
  return event;
}

export function initSentry(location = window.location) {
  if (isDevHost(location)) return false;

  Sentry.init({
    dsn: DSN,
    environment: "production",
    sampleRate: ERROR_SAMPLE_RATE,
    // Errors only. No performance tracing or session replay — each carries its
    // own quota, and replay adds privacy and CSP surface. Add later if wanted.
    denyUrls: THIRD_PARTY_DENY_URLS,
    ignoreErrors: IGNORE_ERRORS,
    beforeSend
  });
  return true;
}
